import React from 'react';
import { useTheme } from '@mui/material';
import { AbstractButton } from '../AbstractButton';
import { Icon } from '../Icon';
import { Flex } from '../Flex';
import { InputControl, InputElement, InputLabel } from '../utils/Input';
import { isEmptyValue, noop, slugify } from '../../utils/helpers';
import { themePalette } from '../../theme/theme.palette';

const BASE_INPUT_SX = {
  paddingTop: '20px',
  paddingBottom: '7.5px',
  borderRadius: '6px',
} as const;

const HIDE_NATIVE_SPINNERS_SX = {
  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  MozAppearance: 'textfield',
} as const;

export interface NumberInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Callback when the number changes */
  onInputNumberChange?: (e: number) => void;

  /** Callback when the number changes */
  onChange?: (e: React.FormEvent<HTMLInputElement>) => void;

  /**
   * The value of the input number.
   * */
  value: number | null;

  /** The label that is associated with this input */
  label: string;

  /** Whether the input has an invalid value */
  invalid?: boolean;

  /** Whether the input is required or not */
  required?: boolean;

  /** Whether the input is disabled or not */
  disabled?: boolean;

  /** Hide the increment/decrement spinner buttons. Keyboard stepping still works. */
  hideSpinners?: boolean;

  /** The max number that the input can accept */
  max?: number;

  /** The min number that the input can accept */
  min?: number;

  /** The step in which the input increases */
  step?: number;
}

/**
 * A number input is a typical HTML <input> for numbers
 */
export const NumberInput: React.FC<NumberInputProps> = ({
  label,
  invalid,
  required,
  disabled,
  hideSpinners = false,
  id,
  name,
  value = 0,
  max,
  min,
  onInputNumberChange = noop,
  onChange = noop,
  ...rest
}) => {
  const [inputNumberValue, setInputNumberValue] = React.useState<number | null>(value ?? null);
  const [isFocused, setIsFocused] = React.useState(false);

  const _ref = React.useRef<HTMLInputElement>(null);

  const theme = useTheme();
  const { componentTokens } = themePalette(theme.palette.mode);

  React.useLayoutEffect(() => {
    setInputNumberValue(value);
  }, [value]);

  const _onChange = React.useCallback(
    (num: number | null): void => {
      onInputNumberChange(num);
      setInputNumberValue(num);
    },
    [onInputNumberChange, setInputNumberValue],
  );

  const stepUp = React.useCallback(() => {
    if (_ref.current) {
      // If current value is null/empty, set it to 0 first, then step up
      if (_ref.current.value === '') {
        _ref.current.value = '0';
      }
      _ref.current.stepUp();
      _onChange(Number(_ref.current.value));
    }
  }, [_onChange]);

  const stepDown = React.useCallback(() => {
    if (_ref.current) {
      // If current value is null/empty, set it to 0 first, then step down
      if (_ref.current.value === '') {
        _ref.current.value = '0';
      }
      _ref.current.stepDown();
      _onChange(Number(_ref.current.value));
    }
  }, [_onChange]);

  const identifier = id || name || slugify(label);

  const isLabelRaised = isFocused || !isEmptyValue(value);

  const inputNumberProps = {
    ...rest,
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      rest.onFocus?.(e);
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      rest.onBlur?.(e);
    },
    onChange: (e: React.FormEvent<HTMLInputElement>) => {
      const stringValue = e.currentTarget.value;
      const numericValue = stringValue === '' ? null : Number(stringValue);
      setInputNumberValue(numericValue);
      _onChange(numericValue);
      onChange(e);
    },
  };

  return (
    <InputControl invalid={invalid} disabled={disabled} required={required}>
      <InputElement
        type="number"
        role="spinbutton"
        id={identifier}
        name={name}
        value={inputNumberValue ?? ''}
        aria-valuenow={value !== undefined ? Number(value) : undefined}
        max={max}
        aria-valuemax={max}
        min={min}
        aria-valuemin={min}
        ref={_ref}
        standalone={!label}
        {...inputNumberProps}
        sx={{
          ...BASE_INPUT_SX,
          ...(hideSpinners && HIDE_NATIVE_SPINNERS_SX),
        }}
      />
      {label && (
        <InputLabel raised={isLabelRaised} htmlFor={identifier}>
          {label}
        </InputLabel>
      )}
      {!hideSpinners && (
        <Flex
          direction="column"
          position="absolute"
          top={0}
          right={0}
          height="100%"
          color={componentTokens.inputs.colors.normal.assets.input.resting}
          borderLeft={`1px solid ${componentTokens.inputs.colors.normal.stroke.resting}`}
          overflow="hidden"
        >
          <AbstractButton aria-label="Increment" aria-hidden tabIndex={-1} onClick={stepUp}>
            <Icon type="caret-up" size="large" />
          </AbstractButton>
          <AbstractButton aria-label="Decrement" aria-hidden tabIndex={-1} onClick={stepDown}>
            <Icon type="caret-down" size="large" />
          </AbstractButton>
        </Flex>
      )}
    </InputControl>
  );
};
